import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin,
  Flag,
  Search,
  Check,
  Mic,
  PenLine,
  Camera
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { CarouselSurface } from './carousel/CarouselSurface';
import { LoopingDeck, CarouselArrows, wrapCarouselIndex } from './carousel/LoopingDeck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact, looping score-entry setup; browsing never submits a round.
 * Steps: 1. Holes (9/18) → 2. Starting Nine → 3. Course → 4. Tee Box → 5. Entry Method → Start
 */
const ScoreSetupCarousel = ({
  onComplete,
  onBack,
  onExit = onBack,
  user,
  registeredCourses = [],
  initialCourse = null
}) => {
  // Current carousel step
  const [currentStep, setCurrentStep] = useState(0);
  
  // Setup selections
  const [numberOfHoles, setNumberOfHoles] = useState(initialCourse?.holes === 9 ? 9 : 18);
  const [startingNine, setStartingNine] = useState('front');
  const [selectedCourse, setSelectedCourse] = useState(initialCourse);
  const [selectedTee, setSelectedTee] = useState(initialCourse?.tees?.[0] || null);
  const [entryMethod, setEntryMethod] = useState('manual');
  
  // Course search state
  const [courseSearch, setCourseSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [availableTees, setAvailableTees] = useState(initialCourse?.tees || []);
  
  const audioContextRef = useRef(null);
  const advanceTimer = useRef(null);
  const activeStep = useRef('holes');
  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      if (audioContext.state === 'suspended') audioContext.resume();
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.1);
      gainNode.gain.setValueAtTime(0.05, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {}
  }, []);

  // Define steps - conditionally include Starting Nine only for 9-hole rounds
  const getSteps = () => {
    const steps = [
      { id: 'holes', title: 'How Many Holes?', subtitle: 'Select your round length' },
    ];
    
    // Only show Starting Nine step if 9 holes selected
    if (numberOfHoles === 9) {
      steps.push({ id: 'nine', title: 'Which Nine?', subtitle: 'Front 9 or Back 9' });
    }
    
    steps.push(
      { id: 'course', title: 'Select Course', subtitle: 'Find your golf course' },
      { id: 'tee', title: 'Select Tee Box', subtitle: 'Choose your tees for yardages' },
      { id: 'method', title: 'Entry Method', subtitle: 'How do you want to enter scores?' },
      { id: 'start', title: 'Ready to Play', subtitle: 'Review and start' }
    );
    
    return steps;
  };
  
  const steps = getSteps();
  const totalSteps = steps.length;
  activeStep.current = steps[currentStep]?.id;

  // Search for courses using the voice lookup API
  const searchCourses = async () => {
    if (!courseSearch.trim() || courseSearch.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await axios.post(`${API}/voice/lookup-course`, {
        course_name: courseSearch
      });
      
      if (response.data.success) {
        if (response.data.multiple_results && response.data.courses) {
          // Multiple results - show list
          setSearchResults(response.data.courses);
        } else if (response.data.course) {
          // Single result - auto-select
          const course = response.data.course;
          setSearchResults([{
            id: course.id,
            club_name: course.name || course.club_name,
            course_name: course.course_name,
            location: course.location,
            ...course
          }]);
        } else {
          setSearchResults([]);
          toast.error("No courses found");
        }
      } else {
        setSearchResults([]);
        toast.error(response.data.message || "No courses found");
      }
    } catch (error) {
      console.error("Course search error:", error);
      toast.error("Failed to search courses");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Select a course from search results
  const selectCourse = async (course) => {
    setIsSearching(true);
    setCourseSearch('');
    
    try {
      // Fetch full course details from GolfCourseAPI
      const response = course._registered ? { data: { success: true, course } } : await axios.get(`${API}/courses/golfapi/${course.id}`);
      
      if (response.data.success && response.data.course) {
        const courseData = response.data.course;
        
        // Set the full course data
        setSelectedCourse({
          id: courseData.course_id || course.id,
          course_id: courseData.course_id || course.id,
          club_name: courseData.name || course.club_name,
          name: courseData.name || course.club_name,
          location: courseData.location || course.location,
          par: courseData.par,
          hole_pars: courseData.hole_pars || [],
          hole_handicaps: courseData.hole_handicaps || [],
          tees: courseData.tees || []
        });
        
        // Set available tees
        setAvailableTees(courseData.tees || []);
        if (courseData.tees && courseData.tees.length > 0) {
          // Auto-select first tee with yardages
          const defaultTee = courseData.tees.find(t => t.hole_yardages?.length > 0) || courseData.tees[0];
          setSelectedTee(defaultTee);
        } else setSelectedTee(null);
        
        setSearchResults([]);
        
        // Auto-advance to next step
        if (activeStep.current === 'course') goToNextStep();
      } else {
        toast.error("Failed to fetch course details");
      }
    } catch (error) {
      console.error("Failed to fetch course details:", error);
      toast.error("Failed to fetch course details");
    } finally {
      setIsSearching(false);
    }
  };

  // Navigation
  const goToStep = index => { playSwoosh(); setCurrentStep(wrapCarouselIndex(index, totalSteps)); };
  const goToNextStep = () => goToStep(currentStep + 1);
  const goToPrevStep = () => goToStep(currentStep - 1);
  const advanceSelection = () => {
    clearTimeout(advanceTimer.current);
    const from = activeStep.current;
    advanceTimer.current = setTimeout(() => { if (activeStep.current === from) goToNextStep(); }, 150);
  };

  // Complete setup and start
  const handleStart = () => {
    if (activeStep.current !== 'start') return;
    onComplete({
      numberOfHoles,
      startingNine: numberOfHoles === 18 ? 'front' : startingNine,
      course: selectedCourse,
      tee: selectedTee,
      entryMethod,
      holeYardages: selectedTee?.hole_yardages || []
    });
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step.id) {
      case 'holes':
        return (
          <div className="flex flex-col items-center justify-center">
            <div className="carousel-choice-pair">
              <button
                onClick={() => { setNumberOfHoles(9); advanceSelection(); }}
                data-testid="score-setup-holes-9"
                className={`w-32 h-32 rounded-2xl flex flex-col items-center justify-center transition-all ${
                  numberOfHoles === 9
                    ? 'bg-gradient-to-br from-emerald-600 to-green-700 text-white shadow-lg shadow-emerald-500/30 scale-105'
                    : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                }`}
              >
                <span className="text-4xl font-black">9</span>
                <span className="text-sm font-medium opacity-80">Holes</span>
              </button>
              <button
                onClick={() => { setNumberOfHoles(18); setStartingNine('front'); advanceSelection(); }}
                data-testid="score-setup-holes-18"
                className={`w-32 h-32 rounded-2xl flex flex-col items-center justify-center transition-all ${
                  numberOfHoles === 18
                    ? 'bg-gradient-to-br from-emerald-600 to-green-700 text-white shadow-lg shadow-emerald-500/30 scale-105'
                    : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                }`}
              >
                <span className="text-4xl font-black">18</span>
                <span className="text-sm font-medium opacity-80">Holes</span>
              </button>
            </div>
          </div>
        );

      case 'nine':
        return (
          <div className="flex flex-col items-center justify-center">
            <div className="carousel-choice-pair">
              <button
                onClick={() => { setStartingNine('front'); advanceSelection(); }}
                data-testid="score-setup-nine-front"
                className={`w-36 h-28 rounded-2xl flex flex-col items-center justify-center transition-all ${
                  startingNine === 'front'
                    ? 'bg-gradient-to-br from-emerald-600 to-green-700 text-white shadow-lg shadow-emerald-500/30 scale-105'
                    : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                }`}
              >
                <span className="text-2xl font-bold">Front 9</span>
                <span className="text-xs opacity-70">Holes 1-9</span>
              </button>
              <button
                onClick={() => { setStartingNine('back'); advanceSelection(); }}
                data-testid="score-setup-nine-back"
                className={`w-36 h-28 rounded-2xl flex flex-col items-center justify-center transition-all ${
                  startingNine === 'back'
                    ? 'bg-gradient-to-br from-emerald-600 to-green-700 text-white shadow-lg shadow-emerald-500/30 scale-105'
                    : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                }`}
              >
                <span className="text-2xl font-bold">Back 9</span>
                <span className="text-xs opacity-70">Holes 10-18</span>
              </button>
            </div>
          </div>
        );

      case 'course':
        return (
          <div className="flex flex-col">
            {registeredCourses.length > 0 && <label className="mb-4 text-xs text-emerald-200">Par Seeker courses
              <select value="" disabled={isSearching} onChange={e => {
                const course = registeredCourses.find(c => String(c.course_id) === e.target.value);
                if (course) selectCourse({ ...course, id: course.course_id, _registered: true });
              }} className="mt-2 w-full min-h-11 rounded-md border border-emerald-800 bg-emerald-950 p-2 text-white" data-testid="score-setup-registered-course">
                <option value="">Select a registered course</option>
                {registeredCourses.map(c => <option key={c.course_id} value={c.course_id}>{c.name}</option>)}
              </select>
            </label>}
            {/* Search Input */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--ps-text-muted)]" />
              <Input
                type="text"
                placeholder="Search course name..."
                value={courseSearch}
                onChange={(e) => setCourseSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchCourses()}
                className="pl-10 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)]"
                data-testid="course-search-input"
              />
              {courseSearch && (
                <button
                  onClick={searchCourses}
                  data-testid="score-setup-search-submit"
                  disabled={isSearching}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-emerald-600 text-white text-sm rounded-lg"
                >
                  {isSearching ? '...' : 'Search'}
                </button>
              )}
            </div>

            {/* Selected Course */}
            {selectedCourse && !searchResults.length && (
              <div data-testid="score-setup-selected-course" className="mb-4 p-4 rounded-xl bg-emerald-900/30 border border-emerald-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-white">{selectedCourse.club_name || selectedCourse.name}</p>
                    {selectedCourse.location && (
                      <p className="text-sm text-emerald-300 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {selectedCourse.location}
                      </p>
                    )}
                  </div>
                  <Check className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2">
                {searchResults.map((course, idx) => (
                  <button
                    key={course.id || idx}
                    onClick={() => selectCourse(course)}
                    data-testid={`score-setup-course-result-${idx}`}
                    disabled={isSearching}
                    className="w-full text-left p-3 rounded-xl bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)] transition-colors disabled:opacity-50"
                  >
                    <p className="font-medium text-[var(--ps-text-primary)]">{course.club_name || course.name}</p>
                    {course.course_name && course.course_name !== course.club_name && (
                      <p className="text-sm text-emerald-400">{course.course_name}</p>
                    )}
                    {course.location && (
                      <p className="text-sm text-[var(--ps-text-muted)] flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {course.location}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Loading State */}
            {isSearching && (
              <div data-testid="score-setup-search-loading" className="flex flex-col items-center justify-center py-4">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-[var(--ps-text-secondary)]">Searching courses...</p>
              </div>
            )}

            {/* Empty State */}
            {!selectedCourse && !searchResults.length && !isSearching && (
              <div data-testid="score-setup-search-empty" className="flex flex-col items-center justify-center text-center py-4">
                <div className="w-16 h-16 rounded-full bg-emerald-900/30 flex items-center justify-center mb-4">
                  <MapPin className="w-8 h-8 text-emerald-500" />
                </div>
                <p className="text-[var(--ps-text-secondary)]">Search for your golf course</p>
                <p className="text-sm text-[var(--ps-text-muted)]">Or skip to enter scores without course data</p>
              </div>
            )}
          </div>
        );

      case 'tee':
        return (
          <div className="flex flex-col items-center justify-center">
            {availableTees.length > 0 ? (
              <div className="w-full max-w-sm space-y-3">
                {availableTees.map((tee, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setSelectedTee(tee); advanceSelection(); }}
                    data-testid={`score-setup-tee-${idx}`}
                    className={`w-full p-4 rounded-xl flex items-center justify-between transition-all ${
                      selectedTee?.name === tee.name
                        ? 'bg-gradient-to-r from-emerald-600 to-green-700 text-white shadow-lg'
                        : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Flag className={`w-5 h-5 ${selectedTee?.name === tee.name ? 'text-white' : 'text-emerald-500'}`} />
                      <span className="font-bold">{tee.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{tee.total_yards?.toLocaleString()} yds</p>
                      <p className="text-xs opacity-70">
                        Rating: {tee.course_rating} / Slope: {tee.slope_rating}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
                  <Flag className="w-8 h-8 text-amber-500" />
                </div>
                <p className="text-[var(--ps-text-secondary)]">No tee data available</p>
                <p className="text-sm text-[var(--ps-text-muted)] mb-4">Yardages won't be shown on hole cards</p>
                <Button data-testid="score-setup-tee-continue" onClick={goToNextStep} className="bg-emerald-600 hover:bg-emerald-700">
                  Continue Anyway
                </Button>
              </div>
            )}
          </div>
        );

      case 'method':
        return (
          <div className="flex flex-col items-center justify-center">
            <div className="w-full max-w-sm space-y-3">
              <button
                onClick={() => { setEntryMethod('manual'); advanceSelection(); }}
                data-testid="score-setup-method-manual"
                className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all ${
                  entryMethod === 'manual'
                    ? 'bg-gradient-to-r from-emerald-600 to-green-700 text-white shadow-lg'
                    : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  entryMethod === 'manual' ? 'bg-white/20' : 'bg-emerald-900/30'
                }`}>
                  <PenLine className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-bold">Manual Entry</p>
                  <p className="text-sm opacity-70">Tap to enter each hole score</p>
                </div>
              </button>
              
              <button
                onClick={() => { setEntryMethod('voice'); advanceSelection(); }}
                data-testid="score-setup-method-voice"
                className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all ${
                  entryMethod === 'voice'
                    ? 'bg-gradient-to-r from-emerald-600 to-green-700 text-white shadow-lg'
                    : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  entryMethod === 'voice' ? 'bg-white/20' : 'bg-emerald-900/30'
                }`}>
                  <Mic className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-bold">Voice Entry</p>
                  <p className="text-sm opacity-70">Speak your scores hands-free</p>
                </div>
              </button>
              
              <button
                onClick={() => { setEntryMethod('scan'); advanceSelection(); }}
                data-testid="score-setup-method-scan"
                className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all ${
                  entryMethod === 'scan'
                    ? 'bg-gradient-to-r from-emerald-600 to-green-700 text-white shadow-lg'
                    : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-primary)] hover:bg-[var(--ps-bg-hover)]'
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  entryMethod === 'scan' ? 'bg-white/20' : 'bg-emerald-900/30'
                }`}>
                  <Camera className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-bold">Scan Scorecard</p>
                  <p className="text-sm opacity-70">AI reads your paper scorecard</p>
                </div>
              </button>
            </div>
          </div>
        );

      case 'start':
        return (
          <div className="flex flex-col items-center justify-center">
            {/* Summary Card */}
            <div data-testid="score-setup-summary" className="w-full max-w-sm pb-4 mb-4 border-b border-emerald-500/30">
              <h3 className="text-lg font-bold text-white mb-4 text-center">Round Summary</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-300">Holes</span>
                  <span className="font-bold text-white">{numberOfHoles}</span>
                </div>
                
                {numberOfHoles === 9 && (
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300">Starting</span>
                    <span className="font-bold text-white">{startingNine === 'front' ? 'Front 9' : 'Back 9'}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-emerald-300">Course</span>
                  <span className="font-bold text-white text-right text-sm max-w-[60%] break-words">
                    {selectedCourse?.club_name || selectedCourse?.name || 'Not selected'}
                  </span>
                </div>
                
                {selectedTee && (
                  <div className="flex justify-between items-center">
                    <span className="text-emerald-300">Tees</span>
                    <span className="font-bold text-white">{selectedTee.name} ({selectedTee.total_yards?.toLocaleString()}y)</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-emerald-300">Entry</span>
                  <span className="font-bold text-white capitalize">{entryMethod}</span>
                </div>
              </div>
            </div>
            
            {/* Start Button */}
            <Button
              onClick={handleStart}
              className="w-full max-w-sm h-14 text-lg font-bold bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-emerald-500/30"
              data-testid="start-round-btn"
            >
              Start Round
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return <CarouselSurface id="score-setup-carousel" label="Round setup" onExit={onExit}>
    <div className="carousel-setup-panel">
      <header className="carousel-panel-header">
        <h2 className="carousel-panel-title" data-testid="score-setup-step-title">{steps[currentStep]?.title}</h2>
        <p className="carousel-panel-subtitle" data-testid="score-setup-step-subtitle">{steps[currentStep]?.subtitle}</p>
      </header>
      <LoopingDeck id="score-setup-deck" items={steps} index={currentStep} onIndexChange={goToStep}
        renderItem={step => <div className="carousel-step-body" data-testid={`score-setup-content-${step.id}`}>{renderStepContent(step)}</div>} />
      {steps[currentStep]?.id === 'course' && !selectedCourse && <div className="text-center">
        <Button variant="ghost" data-testid="score-setup-skip-course" onClick={goToNextStep}>Skip course</Button>
      </div>}
      <CarouselArrows id="score-setup" previous={goToPrevStep} next={goToNextStep} />
      {String(selectedCourse?.course_id || selectedCourse?.id || '').startsWith('top10_') && <a href="https://opengolfapi.org/attribution" target="_blank" rel="noopener noreferrer" className="block text-[10px] text-[var(--ps-text-secondary)] underline text-center mt-2" data-testid="score-setup-open-data-attribution">© OpenStreetMap contributors (ODbL 1.0) via OpenGolfAPI</a>}
    </div>
  </CarouselSurface>;
};

export default ScoreSetupCarousel;
